import React, { useEffect, useMemo, useState } from "react";
import Icon from "../../../components/Icon";
import { useNavigate } from "react-router-dom";
import PanelTemplate from "../../../templates/Panel";
import PageHeader from "../../../components/PageHeader";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { hasPermission, hasPlanFeature } from "../../../services/auth";
import { convertIntToMoney, formatCurrencyShort } from "../../../helpers/conversions";
import PipelineMembersModal from "./PipelineMembersModal";
import PipelineDeleteModal from "./PipelineDeleteModal";
import PipelineMembersAvatars from "../../../components/PipelineMembersAvatars";
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    useSortable,
    rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Cor única de acento para todos os painéis (padrão da marca).
const ACCENT = '#362B7A';

// Normaliza texto para busca: minúsculo e sem acentos, para "Produção" casar
// com "producao". O range ̀-ͯ cobre os diacríticos combinantes que a
// forma NFD separa das letras.
const DIACRITICS = /[̀-ͯ]/g;
const normalize = str => (str ?? '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(DIACRITICS, '');

// Pesos das fatias do mini-funil: usa os cards em aberto por etapa quando o
// backend informa; sem dados (ou tudo zerado), desenha segmentos iguais.
const stageWeights = stages => {
    const counts = stages.map(stage => Number(stage.open_cards) || 0);
    return counts.some(count => count > 0) ? counts : stages.map(() => 1);
};

// Cartão de painel (apresentacional). Quando `dragHandleProps` é fornecido,
// exibe a alça de arraste para reordenar; `innerRef`/`style` vêm do @dnd-kit.
const PipelineCard = ({
    pipeline,
    innerRef,
    style,
    isDragging,
    dragHandleProps,
    openMenuId,
    setOpenMenuId,
    onOpen,
    onEdit,
    onMembers,
    onDelete,
}) => {
    const stages = pipeline.stages ?? [];
    const accent = ACCENT;
    const openCards = pipeline.open_cards ?? 0;
    const openValue = pipeline.open_value ?? 0;
    const automations = pipeline.automations_count ?? 0;
    const weights = stageWeights(stages);
    const showAllStageLabels = stages.length > 0 && stages.length <= 4;

    return (
        <div
            className={`pipeline-card-wrap${isDragging ? ' is-dragging' : ''}`}
            ref={innerRef}
            style={style}
        >
            <button
                type="button"
                className="pipeline-card"
                style={{ '--accent': accent }}
                onClick={() => onOpen(pipeline.id)}
            >
                <span className="pipeline-card__head">
                    <span className="pipeline-card__icon" aria-label={pipeline.name}>
                        {pipeline.icon
                            ? <span className="pipeline-card__emoji" aria-hidden="true">{pipeline.icon}</span>
                            : (pipeline.name ?? '?').trim().charAt(0).toUpperCase()}
                    </span>
                    <span className="pipeline-card__title">
                        <span className="pipeline-card__name">{pipeline.name}</span>
                        <span className="pipeline-card__meta">
                            {stages.length} etapa{stages.length === 1 ? '' : 's'}
                            {' · '}
                            {automations} automa{automations === 1 ? 'ção' : 'ções'}
                        </span>
                    </span>
                </span>

                {stages.length > 0 &&
                    <span className="pipeline-card__funnel">
                        <span className="pipeline-card__funnel-bar">
                            {stages.map((stage, stageIndex) => {
                                const count = Number(stage.open_cards) || 0;
                                const opacity = stages.length === 1
                                    ? 1
                                    : 0.4 + 0.6 * (stageIndex / (stages.length - 1));
                                return (
                                    <span
                                        key={stage.id ?? stageIndex}
                                        className="pipeline-card__funnel-seg"
                                        style={{ flexGrow: weights[stageIndex], background: accent, opacity }}
                                        title={`${stage.name} · ${count.toLocaleString('pt-BR')} em aberto`}
                                    />
                                );
                            })}
                        </span>
                        <span className="pipeline-card__funnel-labels">
                            {showAllStageLabels
                                ? stages.map((stage, stageIndex) => (
                                    <span key={stage.id ?? stageIndex} className="pipeline-card__funnel-label">
                                        {stage.name}
                                    </span>
                                ))
                                : <>
                                    <span className="pipeline-card__funnel-label">{stages[0]?.name}</span>
                                    <span className="pipeline-card__funnel-label">{stages[stages.length - 1]?.name}</span>
                                </>
                            }
                        </span>
                    </span>
                }

                <span className="pipeline-card__stats">
                    <span className="pipeline-card__stat pipeline-card__stat--value">
                        <strong title={convertIntToMoney(openValue, true)}>{formatCurrencyShort(openValue)}</strong>
                        em aberto
                    </span>
                    <span className="pipeline-card__stat pipeline-card__stat--open">
                        <strong>{openCards.toLocaleString('pt-BR')}</strong>
                        em aberto
                    </span>
                </span>

                <span className="pipeline-card__members">
                    <PipelineMembersAvatars members={pipeline.members} />
                </span>
            </button>

            {dragHandleProps &&
                <span
                    className="pipeline-card__drag drag"
                    title="Arraste para reordenar"
                    aria-label="Arraste para reordenar o painel"
                    {...dragHandleProps}
                >
                    <Icon name="drag" size={16} />
                </span>
            }

            <div className="pipeline-card__menu">
                <button
                    type="button"
                    className="pipeline-card__menu-btn"
                    aria-label="Opções do painel"
                    aria-haspopup="true"
                    onClick={() => setOpenMenuId(openMenuId === pipeline.id ? null : pipeline.id)}
                >
                    <Icon name="more-vertical" size={18} />
                </button>
                {openMenuId === pipeline.id &&
                    <>
                        <div className="pipeline-card__menu-overlay" onClick={() => setOpenMenuId(null)} />
                        <div className="pipeline-card__menu-list" role="menu">
                            <button type="button" className="pipeline-card__menu-item" onClick={() => onEdit(pipeline)}>
                                Editar painel
                            </button>
                            <button type="button" className="pipeline-card__menu-item" onClick={() => onMembers(pipeline)}>
                                Usuários com acesso
                            </button>
                            <button type="button" className="pipeline-card__menu-item pipeline-card__menu-item--danger" onClick={() => onDelete(pipeline)}>
                                Excluir
                            </button>
                        </div>
                    </>
                }
            </div>
        </div>
    );
};

// Envolve o cartão com o @dnd-kit para reordenar por arrasta e solta. O arraste
// só inicia pela alça (a listeners/attributes vão só nela), preservando o clique
// no corpo do cartão.
const SortablePipelineCard = (props) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.pipeline.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 5 : undefined,
    };

    return (
        <PipelineCard
            {...props}
            innerRef={setNodeRef}
            style={style}
            isDragging={isDragging}
            dragHandleProps={{ ...attributes, ...listeners }}
        />
    );
};

export const PipelinesList = () => {

    const navigate = useNavigate();

    const [pipelines, setPipelines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [membersTarget, setMembersTarget] = useState(null);
    const [search, setSearch] = useState('');

    const sensors = [useSensor(PointerSensor, { activationConstraint: { distance: 5 } })];

    const summary = useMemo(() => pipelines.reduce((acc, pipeline) => {
        acc.openValue += Number(pipeline.open_value) || 0;
        acc.openCards += Number(pipeline.open_cards) || 0;
        acc.totalCards += Number(pipeline.total_cards) || 0;
        acc.stages += (pipeline.stages ?? []).length;
        return acc;
    }, { openValue: 0, openCards: 0, totalCards: 0, stages: 0 }), [pipelines]);

    const searchTerm = normalize(search.trim());
    const isSearching = searchTerm.length > 0;
    const visiblePipelines = useMemo(
        () => isSearching
            ? pipelines.filter(pipeline => normalize(pipeline.name).includes(searchTerm))
            : pipelines,
        [pipelines, isSearching, searchTerm]
    );

    useEffect(() => {
        getPipelines();
    }, []);

    const getPipelines = () => {
        setLoading(true);
        api.get(`/pipeline`).then(res => {
            setPipelines(res.data.resources ?? []);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    };

    const openPipeline = id => navigate(`/crm/painel/${id}`);
    const createPipeline = () => navigate(`/crm/painel/novo`);

    // Abre a página de edição do painel (/crm/painel/:id/editar).
    const editPipeline = pipeline => {
        setOpenMenuId(null);
        navigate(`/crm/painel/${pipeline.id}/editar`);
    };

    const openMembers = pipeline => {
        setOpenMenuId(null);
        setMembersTarget(pipeline);
    };

    const askDelete = pipeline => {
        setOpenMenuId(null);
        setDeleteTarget(pipeline);
    };

    // Reordena os painéis por arrasta e solta e persiste a nova ordem. Atualiza
    // otimista e reverte se o backend falhar.
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = pipelines.findIndex(p => p.id === active.id);
        const newIndex = pipelines.findIndex(p => p.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        const previous = pipelines;
        const reordered = arrayMove(pipelines, oldIndex, newIndex);
        setPipelines(reordered);

        api.put(`/pipeline/reorder`, { ids: reordered.map(p => p.id) })
            .catch(error => {
                setPipelines(previous);
                renderError(error);
            });
    };

    const cardHandlers = {
        openMenuId,
        setOpenMenuId,
        onOpen: openPipeline,
        onEdit: editPipeline,
        onMembers: openMembers,
        onDelete: askDelete,
    };

    return (
        <PanelTemplate id="crm-pipelines">
            <PageHeader
                left={<h1 className="pipelines-title">Painéis</h1>}
                right={
                    <>
                        {hasPlanFeature('crm') && hasPermission('edit_crm_settings') &&
                            <Button
                                type={`secondary`}
                                size={`small`}
                                svg={`zap`}
                                btnClass={`pipelines-config-btn`}
                                text={`Automações`}
                                link={`/crm/automacoes`}
                            />
                        }
                        {pipelines.length > 0 &&
                            <Button
                                type={`primary`}
                                size={`small`}
                                svg={`plus-white`}
                                text={`Criar painel`}
                                action={createPipeline}
                            />
                        }
                    </>
                }
            />

            {loading &&
                <div className="pipelines-grid">
                    {[0, 1, 2, 3].map(i => (
                        <div key={i} className="pipeline-card pipeline-card--skeleton" />
                    ))}
                </div>
            }

            {!loading && pipelines.length === 0 &&
                <div className="pipelines-empty">
                    <h2>Crie seu primeiro painel</h2>
                    <p>Você ainda não criou nenhum painel. Crie um painel para começar a organizar suas operações.</p>
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg={`plus-white`}
                        text={`Criar painel`}
                        action={createPipeline}
                    />
                </div>
            }

            {!loading && pipelines.length > 0 &&
                <>
                    <div className="pipelines-summary">
                        <div className="pipelines-summary__item">
                            <span className="pipelines-summary__label">Valor em aberto</span>
                            <span className="pipelines-summary__value">{formatCurrencyShort(summary.openValue)}</span>
                            <span className="pipelines-summary__hint">
                                {pipelines.length} pain{pipelines.length === 1 ? 'el' : 'éis'}
                            </span>
                        </div>
                        <div className="pipelines-summary__item">
                            <span className="pipelines-summary__label">Cards em aberto</span>
                            <span className="pipelines-summary__value">{summary.openCards.toLocaleString('pt-BR')}</span>
                            <span className="pipelines-summary__hint">
                                de {summary.totalCards.toLocaleString('pt-BR')} no total
                            </span>
                        </div>
                        <div className="pipelines-summary__item">
                            <span className="pipelines-summary__label">Painéis ativos</span>
                            <span className="pipelines-summary__value">{pipelines.length.toLocaleString('pt-BR')}</span>
                            <span className="pipelines-summary__hint">
                                {summary.stages.toLocaleString('pt-BR')} etapa{summary.stages === 1 ? '' : 's'} no total
                            </span>
                        </div>
                    </div>

                    <div className="pipelines-toolbar">
                        <div className="pipelines-toolbar__search">
                            <Input
                                floating={false}
                                placeholder={`Buscar painéis`}
                                value={search}
                                change={value => setSearch(value)}
                                left={<Icon name="search" size={16} />}
                            />
                        </div>
                        {!isSearching &&
                            <span className="pipelines-toolbar__hint">
                                <Icon name="drag" size={14} /> Arraste os cards para reordenar
                            </span>
                        }
                    </div>

                    {isSearching && visiblePipelines.length === 0 &&
                        <div className="pipelines-empty pipelines-empty--search">
                            <h2>Nenhum painel encontrado</h2>
                            <p>Não encontramos painéis com “{search.trim()}”. Tente outro termo.</p>
                        </div>
                    }

                    <div className="pipelines-grid">
                        {isSearching
                            ? visiblePipelines.map(pipeline => (
                                <PipelineCard key={pipeline.id} pipeline={pipeline} {...cardHandlers} />
                            ))
                            : (
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={visiblePipelines.map(p => p.id)} strategy={rectSortingStrategy}>
                                        {visiblePipelines.map(pipeline => (
                                            <SortablePipelineCard key={pipeline.id} pipeline={pipeline} {...cardHandlers} />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            )
                        }

                        {!isSearching &&
                            <button
                                type="button"
                                className="pipeline-card pipeline-card--new"
                                onClick={createPipeline}
                            >
                                <span className="pipeline-card--new__plus">+</span>
                                <span>Criar painel</span>
                            </button>
                        }
                    </div>
                </>
            }

            {deleteTarget &&
                <PipelineDeleteModal
                    pipeline={deleteTarget}
                    close={() => setDeleteTarget(null)}
                    onDeleted={() => {
                        setDeleteTarget(null);
                        getPipelines();
                    }}
                />
            }

            {membersTarget &&
                <PipelineMembersModal
                    pipeline={membersTarget}
                    close={saved => {
                        setMembersTarget(null);
                        if (saved) getPipelines();
                    }}
                />
            }
        </PanelTemplate>
    );
};

export default PipelinesList;
